import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, MessageCircle, MapPin } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { EMAIL, WHATSAPP_URL, INSTAGRAM_URL } from "@/i18n/translations";
import { trackWhatsApp } from "@/lib/analytics";

const Footer = () => {
  const { t } = useLang();
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-sand-soft pt-20 pb-10"
      itemScope
      itemType="https://schema.org/Organization"
    >
      <meta itemProp="name" content="Moroccan AI Factory" />
      <meta itemProp="url" content="https://maif.ma" />
      <meta itemProp="email" content="contact@moroccanaifactory.com" />
      <meta itemProp="telephone" content="+33624848265" />
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10">
          <div className="col-span-2">
            <div className="flex items-center gap-2">
              <span className="font-accent italic text-2xl text-terracotta leading-none">M</span>
              <span className="font-display tracking-tight text-maif">
                Moroccan AI Factory
              </span>
            </div>
            <p className="mt-4 text-maif-muted max-w-sm text-sm leading-relaxed">
              {t.footer.tagline}
            </p>
            <div
              className="mt-6 flex flex-col gap-2 text-sm"
              itemProp="contactPoint"
              itemScope
              itemType="https://schema.org/ContactPoint"
            >
              <meta itemProp="contactType" content="customer service" />
              <a
                href={`mailto:${EMAIL}`}
                data-testid="footer-email"
                className="inline-flex items-center gap-2 text-sand hover:text-terracotta transition"
                itemProp="email"
              >
                <Mail size={14} aria-hidden="true" /> {EMAIL}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                onClick={() => trackWhatsApp("footer")}
                className="inline-flex items-center gap-2 text-maif-muted hover:text-maif transition"
                itemProp="telephone"
              >
                <MessageCircle size={14} aria-hidden="true" /> WhatsApp · +33 6 24 84 82 65
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="inline-flex items-center gap-2 text-maif-muted hover:text-maif transition"
              >
                <Instagram size={14} aria-hidden="true" /> @moroccan.ai.factory
              </a>
              <span
                className="inline-flex items-center gap-2 text-maif-muted"
                itemProp="address"
                itemScope
                itemType="https://schema.org/PostalAddress"
              >
                <MapPin size={14} aria-hidden="true" />
                <span itemProp="addressCountry">Morocco</span> ·
                <span itemProp="addressRegion">Casablanca · Rabat · Marrakech</span>
              </span>
            </div>
          </div>

          <div>
            <div className="eyebrow">Services</div>
            <ul className="mt-4 space-y-2 text-sm text-maif-muted">
              <li><Link to="/ai-agency-morocco" className="hover:text-maif transition">AI Agency Morocco</Link></li>
              <li><Link to="/ai-agents-development" className="hover:text-maif transition">AI Agents Development</Link></li>
              <li><Link to="/chatbot-development-morocco" className="hover:text-maif transition">Chatbot Development</Link></li>
              <li><Link to="/ai-automation-business" className="hover:text-maif transition">AI Automation</Link></li>
              <li><Link to="/devops-cloud-morocco" className="hover:text-maif transition">DevOps & Cloud</Link></li>
            </ul>
          </div>

          <div>
            <div className="eyebrow">{t.footer.links.product}</div>
            <ul className="mt-4 space-y-2 text-sm text-maif-muted">
              <li><Link to="/#services" className="hover:text-maif transition">LabMind AI</Link></li>
              <li><Link to="/#services" className="hover:text-maif transition">AgentFlow</Link></li>
              <li><Link to="/#services" className="hover:text-maif transition">AI Router</Link></li>
              <li><Link to="/#services" className="hover:text-maif transition">CloudOps</Link></li>
              <li><Link to="/#pricing" className="hover:text-maif transition">Pricing</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow">{t.footer.links.company}</div>
            <ul className="mt-4 space-y-2 text-sm text-maif-muted">
              <li><Link to="/#about" className="hover:text-maif transition">About</Link></li>
              <li><Link to="/#cases" className="hover:text-maif transition">Use cases</Link></li>
              <li><Link to="/blog" className="hover:text-maif transition">Blog</Link></li>
              <li><Link to="/#lead" className="hover:text-maif transition">Contact</Link></li>
            </ul>
          </div>
        </div>

        <div className="relative mt-20 select-none overflow-hidden">
          <div className="font-display font-black tracking-tighter text-[18vw] md:text-[15vw] leading-none uppercase text-[#15151a]">
            MAIF.
          </div>
        </div>

        <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-t border-sand-soft pt-8 text-xs text-maif-muted">
          <div>© {new Date().getFullYear()} Moroccan AI Factory. {t.footer.rights}</div>
          <div className="flex items-center gap-4">
            <span>Morocco · Africa · Worldwide · Est. 2026</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
